import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { ChartContainer } from "@/components/ui/chart";
import { getPlatformAppDistribution } from "@/utils/appAnalytics";
import { Application } from "@/types/application";
import { formatPlatformName } from "@/utils/platformUtils";

interface PlatformAppDistributionChartProps {
  applications: Application[];
}

const PlatformAppDistributionChart = ({ applications }: PlatformAppDistributionChartProps) => {
  const platformData = getPlatformAppDistribution(applications);

  const chartConfig = {
    count: {
      label: "Applications",
    },
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const total = platformData.reduce((sum, item) => sum + item.count, 0);
      const percentage = ((data.count / total) * 100).toFixed(1);
      
      return (
        <div className="bg-background border rounded-lg shadow-md p-3">
          <p className="font-medium">{formatPlatformName(data.platform)}</p>
          <p className="text-sm text-muted-foreground">
            {data.count} apps ({percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    if (percent < 0.05) return null; // Hide label if slice is too small
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize={12}
        fontWeight={500}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <GlassCard variant="strong" hover={false}>
      <GlassCardHeader>
        <GlassCardTitle className="flex items-center gap-2">
          Apps by Platform
          <span className="text-sm font-normal text-muted-foreground">
            Distribution overview
          </span>
        </GlassCardTitle>
      </GlassCardHeader>
      <GlassCardContent>
        <ChartContainer config={chartConfig} className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={platformData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={CustomLabel}
                outerRadius={100}
                fill="#8884d8"
                dataKey="count"
              >
                {platformData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.color}
                  />
                ))}
              </Pie>
              <Tooltip content={CustomTooltip} />
              <Legend 
                verticalAlign="bottom" 
                height={36}
                formatter={(value, entry: any) => (
                  <span style={{ color: entry.color }}>
                    {formatPlatformName(entry.payload.platform)} ({entry.payload.count})
                  </span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </ChartContainer>
      </GlassCardContent>
    </GlassCard>
  );
};

export default PlatformAppDistributionChart;